import {
  URL_ACCOUNT_INVOICING_PAYABLE,
  URL_CANDIDATES,
  URL_CANDIDATES_RECOMMENDED,
  URL_COPY_JOB,
  URL_DASHBOARD,
} from '@seek/adv-constants';
import { createUrlResolver, siteNames } from '@seek/melways-sites';
import { EMPLOYER_CONTACT_US_URL_PROPS } from '@seek/shared-constants';
import { shallowEqual, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { SITE } from 'src/app/constants/configObjectProperties';
import { pageIdToRouteMapping } from 'src/app/routes/pageIdToRouteMapping';
import { getConfig } from 'src/app/utils/config';
import { isStaging } from 'src/app/utils/environment';
import { useConfig } from 'src/app/views/hooks/useConfig';
import { usePostedJobIdSelector } from 'src/app/views/hooks/usePostedJobIdSelector';
import type AppState from 'src/types/appState';

import { getLastValidPage } from './utils/getLastValidPage';

export const useLocalisedRoutes = () => {
  const { currentPageId, formData, sourceJobId, isEditDraft } = useSelector(
    (state: AppState) => ({
      currentPageId: state.app.progress.currentPageId,
      formData: state.formData,
      seekAdvertiserId: state.session.seekAdvertiserId,
      sourceJobId: state.session.sourceJobId,
      isEditDraft:
        state.app.queryParams.referrer?.toLowerCase() === 'editdraft',
    }),
    shallowEqual,
  );

  const history = useHistory();
  const { getLanguage } = useConfig();
  const postedJobId = usePostedJobIdSelector();

  const resolveUrl = createUrlResolver({
    site: getConfig(SITE, siteNames.employer.seek.au),
    language: getLanguage(),
    staging: isStaging(),
  });

  const localiseRoute = (path: string) => resolveUrl({ path });

  const getLastValidRoute = () => {
    const lastValidPage = getLastValidPage(
      formData,
      currentPageId,
      isEditDraft,
    );
    const lastValidRoute = pageIdToRouteMapping[lastValidPage];

    const localisedRoute = localiseRoute(lastValidRoute);
    return localisedRoute;
  };

  const getCandidateJobRoute = () =>
    resolveUrl({
      product: 'candidate',
      path: `/job/${postedJobId}`,
    });

  const createContactUsUrl = () => resolveUrl(EMPLOYER_CONTACT_US_URL_PROPS);

  // Handle local, in-app route navigation and redirects
  // ======================================================
  const goToLocalisedRoute = (path: string) => {
    const localisedRoute = localiseRoute(path);
    history.push(localisedRoute);
  };

  const redirectToLocalisedRoute = (path: string) => {
    const localisedRoute = localiseRoute(path);
    history.replace(localisedRoute);
  };

  // Handle external route navigation and redirects
  // ======================================================
  const goToLocalisedExternalRoute = (path: string) => {
    const localisedRoute = localiseRoute(path);
    window.location.assign(localisedRoute);
  };

  const redirectToLocalisedExternalRoute = (path: string) => {
    const localisedRoute = localiseRoute(path);
    window.location.replace(localisedRoute);
  };

  // Handle page specific navigation and redirects
  // ======================================================
  const redirectToDashboard = () => {
    redirectToLocalisedExternalRoute(URL_DASHBOARD);
  };

  const redirectToJobManagement = (referrer?: string) => {
    const referrerQueryParam = referrer ? `&referrer=${referrer}` : '';
    const redirectUrl = `${URL_CANDIDATES}/?jobid=${sourceJobId}${referrerQueryParam}`;

    redirectToLocalisedExternalRoute(redirectUrl);
  };

  const generateInvoicePayableUrl = () =>
    localiseRoute(URL_ACCOUNT_INVOICING_PAYABLE);

  const redirectToCandidateJobDetailsPage = () => {
    const url = getCandidateJobRoute();
    window.location.replace(url);
  };

  const getCopyJobRoute = (originalJobId: string) =>
    localiseRoute(`${URL_COPY_JOB}?&jobId=${originalJobId}`);

  const getAdProductContactUsUrl = (redirectUrl: string) =>
    localiseRoute(redirectUrl);

  const getRecommendedCandidatesUrl = () =>
    postedJobId
      ? localiseRoute(`${URL_CANDIDATES_RECOMMENDED}?jobId=${postedJobId}`)
      : '#';

  return {
    getCopyJobRoute,
    createContactUsUrl,
    generateInvoicePayableUrl,
    getLastValidRoute,
    getCandidateJobRoute,
    goToRoute: goToLocalisedRoute,
    redirectToRoute: redirectToLocalisedRoute,
    goToExternalRoute: goToLocalisedExternalRoute,
    redirectToDashboard,
    redirectToJobManagement,
    redirectToCandidateJobDetailsPage,
    redirectToExternalRoute: redirectToLocalisedExternalRoute,
    localiseRoute,
    getAdProductContactUsUrl,
    resolveUrl,
    getRecommendedCandidatesUrl,
  };
};
